import type { Step } from 'shepherd.js'

/**
 * Binds per-step side effects to the full set of exit paths.
 *
 * Shepherd fires `step.hide` only when transitioning to another step — NOT
 * when the user cancels (X / Esc) or completes from the active step. Without
 * this helper, every interactive step has to remember to register three
 * cleanups (`step.once('hide')`, `tour.once('cancel')`, `tour.once('complete')`).
 * Forgetting any one strands DOM state when the user dismisses mid-step
 * (e.g. open dropdowns, body[data-*] flags, attached event listeners).
 *
 * `teardown` MUST be idempotent — multiple exit paths can fire in sequence
 * (e.g. cancel then destroy). Guard with a "nothing to do" check at the top.
 *
 * Usage:
 *   when: {
 *     show(this: Step) {
 *       attachInteractiveStep(this, { setup, teardown })
 *     },
 *   }
 */
export function attachInteractiveStep(
  step: Step,
  hooks: { setup: () => void, teardown: () => void },
): void {
  hooks.setup()
  // Self-clearing wrap. Without the explicit `off` calls below, navigating
  // away from the step (hide) leaves the still-pending `once` listeners on
  // `cancel` / `complete` from THIS show; if the user clicks Back and
  // returns to the same step, a new show stacks a second wrap, and teardown
  // fires twice on the next cancel. Removing our own peer listeners when
  // any one of them fires keeps the count at "one wrap, one teardown" per
  // entry.
  const wrap = () => {
    hooks.teardown()
    step.off('hide', wrap)
    step.tour.off('cancel', wrap)
    step.tour.off('complete', wrap)
  }
  step.once('hide', wrap)
  step.tour.once('cancel', wrap)
  step.tour.once('complete', wrap)
}
